import { useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import NoLessonPaper from '../../../paper/NoLessonPaper';
import GridListComponent from '../../../grid/GridList';
import LessonCard from '../../../card/LessonCard';
import LessonFilter from './LessonFilter';
import LessonDetail from './LessonDetail';
import ViewLessonDetailAction from './actions/ViewLessonDetailAction';
import { useLessonCardFilter } from './LessonCardListDetailHooks';
import CardDetailContainer, { useCardDetail } from '../CardDetailContainer';
import './LessonCardListDetail.scss';
import { calculateCardSize } from '../../../../utils/CardSizeCalculator';
import { filterUndefined } from '../../../../utils/FilterUndefinedObject';

const defaultProps = {
  cardSize: calculateCardSize(),
  lessonData: {},
  filterTitle: undefined,
  withArchiveFilter: true,
  preFilterComponent: null,
  noFilter: false,
  onFilterChange: () => { },
  noLessonMessage: 'No Activities Available',
  multiSelectFilterConfig: [],
  createCardAction: () => ([]),
  createCardLabel: () => ([]),
  createCardDetailAction: () => ([]),
  createCardDetailLabel: () => ([]),
  currentUser: {},
  currentUserProfle: {},
  createLessonTagsComponent: () => null,
  createLessonMultiSelectionCompnent: null,
  onLessonSelect: () => { },
  selectedLessonIds: [],
  editSubjectAction: () => { },
  createRowAction: null,
  createLessonDetailAction: () => ([]),
  displayFastNavigator: false,
  showLessonDescription: undefined,
  showLessonBadges: undefined,
  largeSingleRowMode: false,
  currentSelectedSubjectName: '',
  onTitleClick: null,
  createAddtionalDetailPanel: () => null,
  shouldDisplayViewDetail: () => true,
};

const LessonCardListDetail = forwardRef((props, ref) => {
  const {
    cardSize,
    lessonData,
    preFilterComponent,
    noLessonMessage,
    filterTitle,
    withArchiveFilter,
    noFilter,
    onFilterChange,
    multiSelectFilterConfig,
    createCardAction,
    createCardLabel,
    createCardDetailAction,
    createCardDetailLabel,
    currentUser,
    currentUserProfle,
    createLessonTagsComponent,
    createLessonMultiSelectionCompnent,
    onLessonSelect,
    selectedLessonIds,
    editSubjectAction,
    createRowAction,
    createLessonDetailAction,
    displayFastNavigator,
    showLessonDescription,
    showLessonBadges,
    largeSingleRowMode,
    currentSelectedSubjectName,
    onTitleClick,
    createAddtionalDetailPanel,
    shouldDisplayViewDetail,
  } = { ...defaultProps, ...filterUndefined(props) };
  const {
    filteredLessons,
    hasLesson,
    filterValue,
    handleOnFilterChange,
  } = useLessonCardFilter({
    lessonData,
    multiSelectFilterConfig,
    onFilterChange,
    noFilter,
    withArchiveFilter,
  });

  const {
    handleOnViewDetailClick,
    handleOnCloseDetail,
    openCardDetail,
    setOpenCardDetail,
  } = useCardDetail();

  useImperativeHandle(ref, () => ({
    getCurrentDisplayData: () => filteredLessons,
  }));

  const lessonList = useMemo(() => (
    <div className='lesson-card-list-detail-container'>
      <div className='lesson-card-list-detail-header'>
        {preFilterComponent}
        {!noFilter && (
          <LessonFilter
            filterValue={filterValue}
            onFilterChange={handleOnFilterChange}
            multiSelectFilterConfig={multiSelectFilterConfig}
            withArchiveFilter={withArchiveFilter}
            filterTitle={filterTitle}
          />
        )}
        {createLessonMultiSelectionCompnent && createLessonMultiSelectionCompnent()}
      </div>
      {
        hasLesson && (
          filteredLessons.map((subjectWithLessons, rowIndex) => (
            subjectWithLessons.lessons.length > 0
              ? (
                <div
                  className='lesson-list-row'
                  data-dd-privacy='mask'
                  key={`lesson-list-row-${subjectWithLessons.subject}-${JSON.stringify(filterValue || Math.random())}`}
                  data-test={`lesson-list-row-${subjectWithLessons.subject}`}
                >
                  {createRowAction && (
                    <div className='lesson-list-row-component'>
                      {createRowAction(rowIndex)}
                    </div>
                  )}

                  <GridListComponent
                    key={`lesson-list-${subjectWithLessons.subject}-${JSON.stringify(filterValue)}`}
                    titleName={subjectWithLessons.subject}
                    subTitle={subjectWithLessons.subTitle}
                    // slidesToShow={cardSize.slidesToShow}
                    slidesToScroll={cardSize.slidesToScroll}
                    className='lesson-card-list-detail-lessons'
                    canSwipe={false}
                    displayFastNavigator={displayFastNavigator}
                    onTitleClick={onTitleClick ? () => {
                      onTitleClick(subjectWithLessons);
                    } : null}
                  >
                    {
                      subjectWithLessons.lessons.map((lesson, cardIndex) => {
                        const actions = createCardAction(lesson, cardIndex) || [];
                        const cardLabels = createCardLabel(lesson, cardIndex) || [];
                        const key = `vizzle-lesson-detail-${subjectWithLessons.subject}-lesson-index-${cardIndex}`;
                        if (shouldDisplayViewDetail(lesson)) {
                          actions.push(
                            <ViewLessonDetailAction
                              key={key}
                              className={key}
                              onClick={() => { handleOnViewDetailClick(cardIndex, rowIndex); }}
                            />,
                          );
                        }

                        return (
                          <Box
                            key={`lessons-action-id-${lesson.attributes.uuid}`}
                            className='lesson-card-action'
                            onClick={() => onLessonSelect(lesson)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                onLessonSelect(lesson);
                              }
                            }}
                          >
                            <LessonCard
                              key={`lessons-id-${lesson.attributes.uuid}`}
                              lessonData={lesson}
                              width={cardSize.width}
                              actions={actions}
                              isHighligthed={selectedLessonIds.includes(lesson.id)}
                              cardLabels={cardLabels}
                              showLessonDescription={showLessonDescription}
                              showLessonBadges={showLessonBadges}
                            />
                          </Box>
                        );
                      })
                    }
                  </GridListComponent>
                </div>
              ) : ''
          ))
        )
      }
      {
        !hasLesson && largeSingleRowMode && (
          <GridListComponent
            key='lesson-list-no-lesson-placeholder'
            titleName={currentSelectedSubjectName}
            // slidesToShow={cardSize.slidesToShow}
            slidesToScroll={cardSize.slidesToScroll}
            className='lesson-card-list-detail-lessons'
            canSwipe={false}
            displayFastNavigator={displayFastNavigator}
            placeHolderChildren={(
              <div className='no-lesson-placeholder'>
                <div style={{ height: `${cardSize.width * 1.24}px`, display: 'flex', alignItems: 'center' }}>
                  <NoLessonPaper message={noLessonMessage} />
                </div>
              </div>
            )}
          />
        )
      }
      {
        !hasLesson && !largeSingleRowMode && (
          <NoLessonPaper message={noLessonMessage} />
        )
      }
    </div>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [filteredLessons, selectedLessonIds]);

  const cardSlide = useRef(null);

  const lessonDetail = () => (
    openCardDetail.open ? (
      <CardDetailContainer
        ref={cardSlide}
        open={openCardDetail.open}
        selectedIndex={openCardDetail.selectedIndex}
        onClose={handleOnCloseDetail}
        dataList={filteredLessons[openCardDetail.rowIndex].lessons}
        onPageChange={(index) => {
          setOpenCardDetail({
            ...openCardDetail,
            selectedIndex: index,
          });
        }}
        createCardFunction={(lesson, index) => (
          <LessonDetail
            inert={openCardDetail.selectedIndex !== index}
            key={`lesson-detail-${lesson.id}`}
            lesson={lesson}
            currentUser={currentUser}
            currentUserProfle={currentUserProfle}
            included={lessonData.included}
            actions={createCardDetailAction(
              lesson,
              currentUser,
              openCardDetail.rowIndex,
              () => {
                handleOnCloseDetail();
              },
              (cardIndex) => {
                // Move to the selected index
                cardSlide.current.goToSlide(cardIndex, true);
              },
              index,
            )}
            lessonTagsComponent={createLessonTagsComponent(lesson)}
            editSubjectAction={editSubjectAction(lesson, openCardDetail.rowIndex, (newRowIndex) => {
              setOpenCardDetail({
                open: false,
              });
              if (newRowIndex >= 0) {
                setOpenCardDetail({
                  ...openCardDetail,
                  selectedIndex: 0,
                  rowIndex: newRowIndex,
                });
              }
            })}
            lessonDetailActions={createLessonDetailAction(lesson)}
            cardLabels={createCardDetailLabel(lesson)}
            addtionalDetailPanel={createAddtionalDetailPanel({ lesson })}
          />
        )}
      />
    ) : null
  );

  return (
    <Fade in>
      <div>
        {lessonList}
        {lessonDetail()}
      </div>
    </Fade>
  );
});

export default LessonCardListDetail;
